import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { Copy, ExternalLink, Users, Video } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useMeetsProximos } from '@/hooks/useMeetsProximos'
import { abrirEnlace, esEnlaceAbrible } from '@/lib/abrir-enlace'
import { copiarEnlaceMeet } from '@/lib/copiar-enlace'

import type { FranjaMeet } from '@/lib/meets'

const DIAS_HORIZONTE = 7

/**
 * El semáforo que pidió el ticket: un punto de color y, en lo inminente, su etiqueta.
 *
 * Clases completas y literales, porque Tailwind escanea texto e interpolar el color lo dejaría
 * fuera del bundle.
 */
const SEMAFORO: Record<FranjaMeet, { punto: string; etiqueta: string; texto: string }> = {
  // "Hoy" va sólido, con el verde de marca: en tema oscuro el emerald tenue quedaba MENOS
  // visible que el ámbar de mañana, justo al revés de la urgencia que tiene que comunicar.
  //
  // El texto es el verde oscuro de marca y no `primary-foreground`, que es blanco puro: blanco
  // sobre este verde da 2,57:1 y WCAG AA pide 4,5:1 para texto chico. Así queda en 6,56:1.
  hoy: {
    punto: 'bg-primary',
    etiqueta: 'bg-primary text-[#012124]',
    texto: 'Hoy',
  },
  manana: {
    punto: 'bg-amber-500',
    etiqueta: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
    texto: 'Mañana',
  },
  proximo: {
    punto: 'bg-muted-foreground/40',
    etiqueta: '',
    texto: '',
  },
  // Nunca llega acá: `clasificarProximos` filtra los pasados. Está para que el mapa sea total.
  pasado: { punto: '', etiqueta: '', texto: '' },
}

/** El enlace solo se ofrece en lo inminente: en la cola es ruido, y todavía puede cambiar. */
const CON_ENLACE: FranjaMeet[] = ['hoy', 'manana']

export function MeetsCard() {
  const { meets } = useMeetsProximos(DIAS_HORIZONTE)

  // Una tarjeta vacía en la pantalla principal es ruido permanente: en una semana sin
  // reuniones, no va.
  if (meets.length === 0) return null

  // La grilla envuelve a la tarjeta acá y no en Inicio: así desaparece junto con ella. Un
  // contenedor vacío sigue contando como hijo y duplica la separación entre las tarjetas vecinas.
  return (
    <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <Video className="h-4 w-4 text-blue-600" />
            Reuniones
          </CardTitle>
          <Link to="/calendario" className="text-sm text-muted-foreground hover:text-foreground">
            Ver calendario
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {meets.map(({ meet, franja, inicio }) => {
              const estilo = SEMAFORO[franja]
              const todoElDia = meet.todo_el_dia
              return (
                <div key={meet.id} className="rounded-lg bg-muted/40 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 text-sm font-medium">
                        <span
                          className={`h-2 w-2 shrink-0 rounded-full ${estilo.punto}`}
                          aria-hidden="true"
                        />
                        <span className="truncate">{meet.titulo}</span>
                      </p>
                      <p className="mt-0.5 pl-4 text-xs text-muted-foreground">
                        {format(inicio, "EEE d 'de' MMM", { locale: es })}
                        {!todoElDia && ` · ${format(inicio, 'HH:mm')}`}
                      </p>
                    </div>
                    {estilo.texto && (
                      <span
                        className={`shrink-0 rounded-full px-3 py-1 text-sm font-bold ${estilo.etiqueta}`}
                      >
                        {estilo.texto}
                      </span>
                    )}
                  </div>

                  {meet.participantes && meet.participantes.length > 0 && (
                    <p className="mt-1.5 flex items-center gap-1.5 pl-4 text-xs text-muted-foreground">
                      <Users className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">
                        {meet.participantes.map((p) => p.split('@')[0]).join(', ')}
                      </span>
                    </p>
                  )}

                  {CON_ENLACE.includes(franja) && meet.meet_url && (
                    <div className="ml-2 mt-1.5 flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => copiarEnlaceMeet(meet.meet_url!)}
                      >
                        <Copy className="mr-1.5 h-3 w-3" />
                        Copiar enlace
                      </Button>
                      {esEnlaceAbrible(meet.meet_url) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => abrirEnlace(meet.meet_url!)}
                        >
                          <ExternalLink className="mr-1.5 h-3 w-3" />
                          Abrir
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
